import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCcw } from 'lucide-react';
import i18n from '../lib/i18n'; // class komponenta — useTranslation hook nejde, voláme i18n.t() přímo

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.retryTimeout = null;
  }

  componentWillUnmount() {
    if (this.retryTimeout) clearTimeout(this.retryTimeout);
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });

    // Auto-retry po 30s — produkce se nesmí zasekávat
    if (this.retryTimeout) clearTimeout(this.retryTimeout);
    this.retryTimeout = setTimeout(() => {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }, 30_000);

    // Push do ACM Audit Loki — agent to najde v logu
    try {
      const payload = {
        message: error.message,
        stack: error.stack?.slice(0, 2000),
        component_stack: errorInfo.componentStack?.slice(0, 1000),
        url: window.location.pathname,
        timestamp: new Date().toISOString(),
      };
      // Fire-and-forget — same-origin UI capability, nikdy veřejná MCP/API URL.
      fetch('/ui/commands/tool', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tool: 'acm-self.ui_error_report',
          arguments: payload,
        }),
      }).catch(() => {});
    } catch {
      // Logging selhal — nevadí, error je v console
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/core/dashboard';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen bg-bg flex items-center justify-center p-6'>
          <div className='max-w-lg w-full'>
            <div className='rounded-2xl border border-red-500/30 bg-red-600/5 p-6 space-y-4'>
              <div className='flex items-center gap-3'>
                <div className='p-2 rounded-xl bg-red-500/20'>
                  <AlertTriangle size={24} className='text-red-400' />
                </div>
                <div>
                  <h1 className='text-lg font-bold text-white'>
                    {i18n.t('errors.somethingWrong')}
                  </h1>
                  <p className='text-sm text-red-400'>{i18n.t('errors.unexpectedError')}</p>
                </div>
              </div>

              <div className='rounded-xl bg-gray-900/50 border border-gray-800 p-4'>
                <p className='text-xs text-gray-500 font-mono mb-1'>
                  {i18n.t('errors.errorLabel')}
                </p>
                <p className='text-sm text-red-300 font-mono break-all'>
                  {this.state.error?.message || i18n.t('errors.unknownError')}
                </p>
                {this.state.error?.stack && (
                  <details className='mt-2'>
                    <summary className='text-xs text-gray-600 cursor-pointer hover:text-gray-500'>
                      Stack trace
                    </summary>
                    <pre className='mt-2 text-xs text-gray-600 font-mono whitespace-pre-wrap overflow-auto max-h-40'>
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>

              <div className='flex gap-3 pt-2'>
                <button
                  onClick={this.handleReload}
                  className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-semibold text-white transition-colors'
                >
                  <RefreshCcw size={14} />
                  {i18n.t('errors.tryAgain')}
                </button>
                <span className='text-[10px] text-gray-600 self-center'>
                  {i18n.t('errors.autoRetry')}
                </span>
                <button
                  onClick={this.handleGoHome}
                  className='flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-700 text-sm font-semibold text-gray-300 transition-colors'
                >
                  <Home size={14} />
                  {i18n.t('errors.home')}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for error handling in async operations
export function useErrorHandler() {
  return {
    handleError: (error: unknown): string => {
      if (error instanceof Error) {
        return error.message;
      }
      if (typeof error === 'string') {
        return error;
      }
      return i18n.t('errors.unknownError');
    },
  };
}
